import type { ChangeEvent, ComponentProps, FocusEvent, ReactNode } from "react";

import { cn } from "../lib/cn";
import { fieldBase } from "./Input";

export interface NumberRules {
  /** Digits allowed after the point: 0 = whole numbers only (quantities, grams, days), 2 = money and percentages. */
  decimals?: number;
  /** Allow a leading minus (off by default: prices, stock and sizes are never negative). */
  allowNegative?: boolean;
  /** Largest value; also limits how many digits can be typed before the point. */
  max?: number;
}

/**
 * Keeps only what a number field may hold: digits, at most one point (when `decimals` > 0) with at most `decimals`
 * digits after it, an optional leading minus, and no more whole-number digits than `max` has. Grouping commas and
 * spaces from pasted text are dropped ("1,250.505" → "1250.50" with 2 decimals).
 */
export function sanitizeNumber(
  raw: string,
  { decimals = 0, allowNegative = false, max }: NumberRules = {},
): string {
  const negative = allowNegative && raw.trim().startsWith("-");
  const text = raw.replace(/[^\d.]/g, "");
  const point = text.indexOf(".");
  let whole = point === -1 ? text : text.slice(0, point);
  const fraction = point === -1 ? "" : text.slice(point + 1).replace(/\./g, "");
  whole = whole.replace(/^0+(?=\d)/, "");
  if (max !== undefined && Number.isFinite(max)) {
    whole = whole.slice(0, String(Math.floor(Math.abs(max))).length);
  }
  let out = whole;
  if (decimals > 0 && point !== -1) out = `${whole || "0"}.${fraction.slice(0, decimals)}`;
  return (negative ? "-" : "") + out;
}

/** What a finished entry looks like: "12." → "12", "-" → "", ".5" → "0.5". */
export function tidyNumber(value: string): string {
  if (value === "-" || value === ".") return "";
  return value.endsWith(".") ? value.slice(0, -1) : value;
}

export interface NumberInputProps
  extends
    Omit<ComponentProps<"input">, "type" | "inputMode" | "max" | "min">,
    Omit<NumberRules, "max"> {
  /** Shown inside the field on the right, e.g. "₹", "%", "g", "mm", "days". */
  unit?: ReactNode;
  /** Smallest value (checked by the form's rules; shown to assistive tech). */
  min?: number | string;
  /** Largest value; also limits how many digits can be typed before the point. */
  max?: number | string;
}

/**
 * A text box that only accepts a number: letters, symbols, a second point or too many decimals never appear, on
 * typing or pasting. Values stay strings (exactly what was typed), so money keeps its paise; the form's rules check
 * min/max and give the message. Works with React Hook Form's `register` and as a controlled input.
 */
export function NumberInput({
  decimals = 0,
  allowNegative = false,
  max,
  min,
  unit,
  className,
  onChange,
  onBlur,
  ...props
}: NumberInputProps) {
  const limit = max === undefined || max === "" ? undefined : Number(max);
  const rules = { decimals, allowNegative, max: limit };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const clean = sanitizeNumber(input.value, rules);
    if (clean !== input.value) {
      const caret =
        (input.selectionStart ?? input.value.length) - (input.value.length - clean.length);
      input.value = clean;
      input.setSelectionRange?.(Math.max(caret, 0), Math.max(caret, 0));
    }
    onChange?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const tidy = tidyNumber(input.value);
    if (tidy !== input.value) {
      input.value = tidy;
      onChange?.(event as unknown as ChangeEvent<HTMLInputElement>);
    }
    onBlur?.(event);
  };

  const field = (
    <input
      type="text"
      inputMode={decimals > 0 ? "decimal" : "numeric"}
      autoComplete="off"
      aria-valuemin={min === undefined || min === "" ? undefined : Number(min)}
      aria-valuemax={limit}
      className={cn(fieldBase, "h-11 tabular-nums", unit ? "pr-12" : undefined, className)}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
    />
  );
  if (!unit) return field;
  return (
    <div className="relative">
      {field}
      <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-sm text-muted-foreground">
        {unit}
      </span>
    </div>
  );
}
